import { Button, NoResults } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import styles from "./SelectionMenu.module.less";
import { resolveFormattedTextString } from "client/Misc/utils";

import {
	ActionKind,
	reduceToAvailableActions,
	resolveActionTitle
} from "client_legacy/ndaw/actions";
import {
	EmailSelectionAction,
	EmailSelectionActionContext,
	getSelectionActions
} from "../../Actions/selectionActions";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import { EmailResponseKind } from "../../EmailPage/EmailPage";
import { ExchangeEmailId } from "client/Api/Email/common";
import { MessageFlagState, MessageReadState } from "client/Api/Email/email";
import { useUploadAttachmentInProgressDialog } from "../../Dialogs/UploadAttachmentInProgressDialog";

export type SelectionMenuProps = {
	selectionContext: EmailSelectionActionContext;
	isAttachmentUploadInProgress: boolean;
	onClearSelection(): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onFlagStatusChange(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onReadStatusChange(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
};

export const SelectionMenu = (props: SelectionMenuProps): ReactElement => {
	const {
		selectionContext,
		isAttachmentUploadInProgress,
		onClearSelection,
		onMoveEmails,
		onRespondToEmail,
		onFlagStatusChange,
		onReadStatusChange,
		onDeleteEmails
	} = props;

	const { effectiveSelection } = selectionContext;

	const [uploadAttachmentInProgressDialog, openUploadAttachmentInProgressDialog] = useUploadAttachmentInProgressDialog();

	const checkIfNoUploadsInProgressOrShowError = useCallback(
		(): boolean => {
			if (!isAttachmentUploadInProgress) {
				return true;
			}

			openUploadAttachmentInProgressDialog();

			return false;
		},
		[
			isAttachmentUploadInProgress,
			openUploadAttachmentInProgressDialog
		]
	);

	const selectionActions = useMemo(
		(): readonly EmailSelectionAction[] => {
			return getSelectionActions(
				{
					move: onMoveEmails,
					respondToEmail: onRespondToEmail,
					changeFlagStatus: onFlagStatusChange,
					changeReadStatus: onReadStatusChange,
					delete: onDeleteEmails
				},
				checkIfNoUploadsInProgressOrShowError,
				checkIfNoUploadsInProgressOrShowError
			);
		},
		[
			onDeleteEmails,
			onFlagStatusChange,
			onMoveEmails,
			onReadStatusChange,
			onRespondToEmail,
			checkIfNoUploadsInProgressOrShowError
		]
	);

	const actionsToRender: EmailSelectionAction[] = [
		...reduceToAvailableActions(
			selectionActions,
			selectionContext
		),
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: () => {
				onClearSelection();
			}
		}
	];

	return (
		<div className={styles.root}>
			<div>
				<NoResults
					icon="envelope"
					title={
						resolveFormattedTextString({
							template: localize("_XEmailsSelected"),
							replacements: [
								{
									what: "{{Number}}",
									filler: `${effectiveSelection.length}`
								}
							]
						})
					}
					message={localize("_ChooseOneOfTheFollowingActionsInfo")}
					trackingId="selected-emails-info"
				/>
				<div className={styles.actions}>
					{actionsToRender.map((x) => (
						<Button
							key={x.name}
							kind="transparent-for-list"
							icon={x.icon}
							onClick={() => x.execute(selectionContext)}
							text={resolveActionTitle(x.title, selectionContext)}
							trackingId={x.name}
						/>
					))}
				</div>
			</div>
			{uploadAttachmentInProgressDialog}
		</div>
	);
};
SelectionMenu.displayName="SelectionMenu";